/**
 * csf-evidence.js - "was this a real person?" evidence on every sizing capture.
 *
 * custom-size-flow.js posts each sizing event (result_reached, size_adjusted,
 * email_captured, added_to_basket) to the Apps Script capture endpoint. Those
 * rows said what was chosen but nothing about who chose it, so an
 * added-to-basket email could not be told apart from a bot or a test. This
 * adds an `evidence` object to every one of those payloads. It lands in the
 * sheet's `raw` column and the Events tab without any change to the Apps
 * Script, because the script already stores the whole payload.
 *
 * Kept as its own file rather than written into custom-size-flow.js so the
 * sizing panel itself is untouched: this only ever adds a field to a request
 * the panel was already making. It wraps window.fetch, but acts on exactly
 * two kinds of request and passes everything else straight through:
 *
 *  1. A POST to the capture endpoint whose body is a sizing payload: the
 *     evidence object is added to it.
 *  2. The /cart/add.js call that follows an added_to_basket capture. The
 *     capture is held back (up to 10 seconds) until Shopify answers, so the row
 *     records whether Shopify actually accepted the item and the cart token it
 *     went into. A cart token is the strongest single proof: it can be matched
 *     to a real Shopify cart, and on an order it appears as the order's cart
 *     token. If the page is left before Shopify answers, the held capture is
 *     sent on the way out (keepalive), so nothing is lost.
 *
 * Every step is inside try/catch. If any of it fails, the original request goes
 * out unchanged: evidence must never be able to stop a sizing record or an add
 * to basket.
 *
 * Marking your own visits: run this once in the browser console on each of
 * your own devices and every row from that browser carries tester: "mark".
 *     localStorage.setItem('csf:tester', 'mark')
 * Remove it with localStorage.removeItem('csf:tester').
 *
 * What the evidence fields mean, and which ones point to a bot:
 *   webdriver          true when the browser is being driven by automation
 *                      (Selenium, Puppeteer, Playwright). A real visitor's
 *                      browser reports false.
 *   userAgent          the browser's own description. Headless browsers often
 *                      say "HeadlessChrome"; crawlers name themselves.
 *   taps / keys        real pointer presses and key presses on the page. These
 *                      only count events the browser marks as genuinely from
 *                      the user. A basket add with 0 taps and 0 keys did not
 *                      come from someone using the page.
 *   syntheticClicks    clicks fired by script rather than by a person.
 *   mouseMoves         mouse movement events (desktop only, capped).
 *   secondsOnPage      time from the page loading to this event. A person takes
 *                      tens of seconds at least; a bot can take one or two.
 *   viewport / screen  a 0x0 viewport is a hidden or headless browser.
 *   timezone, languages  should agree with where the visitor says they are.
 *   addOutcome         added | rejected | add_failed | page_left | no_add_seen
 *   cartToken          the Shopify cart the item went into.
 *   themeRole          "main" for the live theme; anything else is a preview,
 *                      which only you can open.
 *   tester             set on your own browsers (see above).
 */
(function () {
  'use strict';

  if (window.__csfEvidence) return;
  window.__csfEvidence = true;

  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;

  var loadedAt = Date.now();
  var firstInputAt = null;
  var counts = { taps: 0, keys: 0, syntheticClicks: 0, mouseMoves: 0 };
  var maxScrollY = 0;
  var MOVE_CAP = 5000;

  function markInput() {
    if (firstInputAt === null) firstInputAt = Date.now();
  }

  try {
    var opts = { capture: true, passive: true };
    document.addEventListener('pointerdown', function (e) {
      if (e.isTrusted) { counts.taps++; markInput(); }
    }, opts);
    document.addEventListener('keydown', function (e) {
      if (e.isTrusted) { counts.keys++; markInput(); }
    }, opts);
    /* el.click() from script fires a click with isTrusted false and no
     * pointerdown at all, which is how most simple bots press buttons. */
    document.addEventListener('click', function (e) {
      if (!e.isTrusted) counts.syntheticClicks++;
    }, opts);
    document.addEventListener('pointermove', function (e) {
      if (e.isTrusted && e.pointerType === 'mouse' && counts.mouseMoves < MOVE_CAP) counts.mouseMoves++;
    }, opts);
    window.addEventListener('scroll', function () {
      var y = window.scrollY || window.pageYOffset || 0;
      if (y > maxScrollY) maxScrollY = Math.round(y);
    }, { passive: true });
  } catch (e) { /* evidence is optional */ }

  /* The endpoint is read from the panel's own config, the same value the panel
   * posts to, so this cannot drift from it. Read lazily: the config is in the
   * page body. */
  var endpointCache;
  function endpoint() {
    if (endpointCache !== undefined) return endpointCache;
    endpointCache = null;
    try {
      var node = document.querySelector('[data-csf-config]');
      if (node) endpointCache = JSON.parse(node.textContent).captureEndpoint || null;
    } catch (e) { endpointCache = null; }
    return endpointCache;
  }

  function cookie(name) {
    try {
      var parts = document.cookie ? document.cookie.split('; ') : [];
      for (var i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (parts[i].slice(0, eq) === name) return decodeURIComponent(parts[i].slice(eq + 1));
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  function seconds(fromMs) {
    return fromMs === null ? null : Math.round((Date.now() - fromMs) / 1000);
  }

  function evidence() {
    var n = window.navigator || {};
    var out = { v: 1 };
    try { out.clientTime = new Date().toISOString(); } catch (e) {}
    try { out.userAgent = n.userAgent || ''; } catch (e) {}
    try { out.webdriver = n.webdriver === true; } catch (e) {}
    try { out.languages = (n.languages && n.languages.length ? n.languages : [n.language]).slice(0, 3).join(','); } catch (e) {}
    try { out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) {}
    try { out.screen = screen.width + 'x' + screen.height + '@' + (window.devicePixelRatio || 1); } catch (e) {}
    try { out.viewport = window.innerWidth + 'x' + window.innerHeight; } catch (e) {}
    try { out.touchPoints = n.maxTouchPoints || 0; } catch (e) {}
    out.secondsOnPage = seconds(loadedAt);
    out.secondsSinceFirstInput = seconds(firstInputAt);
    out.taps = counts.taps;
    out.keys = counts.keys;
    out.syntheticClicks = counts.syntheticClicks;
    out.mouseMoves = counts.mouseMoves;
    out.maxScrollY = maxScrollY;
    try { out.visibility = document.visibilityState || ''; } catch (e) {}
    out.cartToken = cookie('cart');
    try { out.themeRole = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || null; } catch (e) {}
    try { out.tester = window.localStorage.getItem('csf:tester'); } catch (e) { out.tester = null; }
    return out;
  }

  function shopRoot() {
    try {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (e) { return '/'; }
  }

  /* ------------------------------------------- the held basket capture */

  var held = null;

  function flush(extra) {
    if (!held) return;
    var h = held;
    held = null;
    clearTimeout(h.timer);
    try {
      var ev = h.payload.evidence || {};
      Object.keys(extra || {}).forEach(function (k) {
        if (extra[k] !== undefined && extra[k] !== null) ev[k] = extra[k];
      });
      h.payload.evidence = ev;
      h.init.body = JSON.stringify(h.payload);
    } catch (e) { /* send what we have */ }
    h.init.keepalive = true;
    try {
      nativeFetch.call(window, h.url, h.init).catch(function (err) {
        console.warn('[csf] capture failed (ignored):', err);
      });
    } catch (e) { /* capture must never break the page */ }
  }

  function isCartAdd(url) {
    var path = String(url || '').split('#')[0].split('?')[0];
    return /\/cart\/add(\.js)?$/.test(path);
  }

  function watchCartAdd(promise) {
    promise.then(function (res) {
      if (!held) return;
      if (!res || !res.ok) {
        flush({ addOutcome: 'rejected', addStatus: res ? res.status : null });
        return;
      }
      return nativeFetch.call(window, shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          flush({
            addOutcome: 'added',
            cartToken: (cart && cart.token) || cookie('cart'),
            cartItemCount: cart ? cart.item_count : null
          });
        })
        .catch(function () { flush({ addOutcome: 'added', cartToken: cookie('cart') }); });
    }).catch(function () {
      flush({ addOutcome: 'add_failed' });
    });
  }

  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var ep = endpoint();

      if (ep && url === ep && init && typeof init.body === 'string') {
        var payload = JSON.parse(init.body);
        if (payload && typeof payload === 'object' && payload.sessionId) {
          payload.evidence = evidence();
          var copy = {};
          Object.keys(init).forEach(function (k) { copy[k] = init[k]; });

          if (payload.status === 'added_to_basket') {
            // A second add before the first resolved: send the first as it is.
            flush({ addOutcome: 'superseded' });
            held = {
              url: url,
              init: copy,
              payload: payload,
              timer: setTimeout(function () { flush({ addOutcome: 'no_add_seen' }); }, 10000)
            };
            // The panel ignores the response; this just keeps its promise happy.
            return Promise.resolve(new Response('{"ok":true,"held":true}', {
              status: 200, headers: { 'Content-Type': 'application/json' }
            }));
          }

          copy.body = JSON.stringify(payload);
          return nativeFetch.call(window, input, copy);
        }
      }

      if (held && isCartAdd(url)) {
        var p = nativeFetch.apply(window, arguments);
        watchCartAdd(p);
        return p;
      }
    } catch (e) {
      /* Fall through to the untouched request. */
    }
    return nativeFetch.apply(window, arguments);
  };

  // Leaving the page (including the panel's form-post fallback) sends anything held.
  window.addEventListener('pagehide', function () { flush({ addOutcome: 'page_left' }); });
})();
